import { useMutation, useQuery } from 'react-query';
import { LimitedSchemaMutationOptions, type ContextlessQueryObserverOptions } from '@frontend/react-query-helpers';
import { SchemaFeatureFlags, SchemaIO } from '@frontend/schema';

export type UpdateFlagIO = SchemaIO<(typeof SchemaFeatureFlags)['UpdateFlag']>;

type UseUpdateFlagMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends UpdateFlagIO['input'] = UpdateFlagIO['input']
> = {
  options?: LimitedSchemaMutationOptions<UpdateFlagIO, E, C, never, RequestOverride>;
};

export type SetFlagForLocationIO = SchemaIO<(typeof SchemaFeatureFlags)['SetFlagForLocation']>;

type UseSetFlagForLocationMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends SetFlagForLocationIO['input'] = SetFlagForLocationIO['input']
> = {
  options?: LimitedSchemaMutationOptions<SetFlagForLocationIO, E, C, never, RequestOverride>;
};

export type DeleteFlagIO = SchemaIO<(typeof SchemaFeatureFlags)['DeleteFlag']>;

type UseDeleteFlagMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends DeleteFlagIO['input'] = DeleteFlagIO['input']
> = {
  options?: LimitedSchemaMutationOptions<DeleteFlagIO, E, C, never, RequestOverride>;
};

export type BatchSetFlagForLocationsIO = SchemaIO<(typeof SchemaFeatureFlags)['BatchSetFlagForLocations']>;

type UseBatchSetForLocationsMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends BatchSetFlagForLocationsIO['input'] = BatchSetFlagForLocationsIO['input']
> = {
  options?: LimitedSchemaMutationOptions<BatchSetFlagForLocationsIO, E, C, never, RequestOverride>;
};

export type SetFlagAllIO = SchemaIO<(typeof SchemaFeatureFlags)['SetFlagAll']>;

type UseSetFlagAllMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends SetFlagAllIO['input'] = SetFlagAllIO['input']
> = {
  options?: LimitedSchemaMutationOptions<SetFlagAllIO, E, C, never, RequestOverride>;
};

export type SetFlagPercentageIO = SchemaIO<(typeof SchemaFeatureFlags)['SetFlagPercentage']>;

type useSetFlagPercentageMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends SetFlagPercentageIO['input'] = SetFlagPercentageIO['input']
> = {
  options?: LimitedSchemaMutationOptions<SetFlagPercentageIO, E, C, never, RequestOverride>;
};

export const getLocationFeatureFlags = ({ locationId }: { locationId: string }) =>
  SchemaFeatureFlags.ListLocationFlags({ locationId });

export const getFeatureFlags = () => SchemaFeatureFlags.ListFlags({});

export const getLocationsWithFlag = ({ flagName, value }: { flagName: string; value: boolean }) =>
  SchemaFeatureFlags.ListLocationsWithFlag({
    flagName,
    value,
  });

export const useQueryLocationFeatureFlags = (locationId: string, options: ContextlessQueryObserverOptions) => {
  return useQuery({
    queryKey: ['location-feature-flags', locationId],
    queryFn: () => getLocationFeatureFlags({ locationId }),
    select: (data) => data.flags,
    ...options,
  });
};

export const useUpdateFlagMutation = ({ options }: UseUpdateFlagMutationArgs = {}) => {
  return useMutation({
    mutationFn: (req: UpdateFlagIO['input']) => SchemaFeatureFlags.UpdateFlag(req),
    ...options,
  });
};

export const useSetFlagForLocationMutation = ({ options }: UseSetFlagForLocationMutationArgs = {}) => {
  return useMutation({
    mutationFn: (req: SetFlagForLocationIO['input']) => SchemaFeatureFlags.SetFlagForLocation(req),
    ...options,
  });
};

export const useDeleteFlagMutation = ({ options }: UseDeleteFlagMutationArgs = {}) => {
  return useMutation({
    mutationFn: (req: DeleteFlagIO['input']) => SchemaFeatureFlags.DeleteFlag(req),
    ...options,
  });
};

export const useBatchSetForLocationsMutation = ({ options }: UseBatchSetForLocationsMutationArgs = {}) => {
  return useMutation({
    mutationFn: (req: BatchSetFlagForLocationsIO['input']) => SchemaFeatureFlags.BatchSetFlagForLocations(req),
    ...options,
  });
};

export const useSetFlagAllMutation = ({ options }: UseSetFlagAllMutationArgs = {}) => {
  return useMutation({
    mutationFn: (req: SetFlagAllIO['input']) => SchemaFeatureFlags.SetFlagAll(req),
    ...options,
  });
};

export const useSetFlagPercentageMutation = ({ options }: useSetFlagPercentageMutationArgs = {}) => {
  return useMutation({
    mutationFn: (req: SetFlagPercentageIO['input']) => SchemaFeatureFlags.SetFlagPercentage(req),
    ...options,
  });
};
